if (!customElements.get('quantity-popover')) {
  customElements.define(
    'quantity-popover',
    class QuantityPopover extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 990px)');
        this.infoButtonDesktop = this.querySelector('.quantity-popover__info-button--icon-only');
        this.infoButtonMobile = this.querySelector('.quantity-popover__info-button--icon-with-label');
        this.popoverInfo = this.querySelector('.quantity-popover__info');
        this.closeButton = this.querySelector('.button-close');
        this.eventMouseEnterHappened = false;

        if (this.closeButton) {
          this.closeButton.addEventListener('click', this.closePopover.bind(this));
        }

        /*
         * Leaving the popover itself closes it at every width. The popover is
         * rendered at all of them -- it is the trigger that is breakpoint-specific:
         * `--icon-only` carries `small-hide medium-hide` and `--icon-with-label`
         * carries `large-up-hide`, so the visible button changes at 990px, which
         * is also what `this.mql` reads.
         */
        if (this.popoverInfo) {
          this.popoverInfo.addEventListener('mouseleave', this.closePopover.bind(this));
        }

        /*
         * Hover-to-open is wired only where the icon-only trigger is on screen.
         * Between 750px and 989px the previous `mqlTablet` check still bound
         * `mouseenter` to that trigger, and `togglePopover` set
         * `eventMouseEnterHappened = true` before discovering it could not act --
         * its `this.mql.matches` test is 990px, and the trigger is `display: none`
         * at this width so the pointer never reaches it anyway. Nothing ever cleared
         * the flag, so the *next* real click on the visible `large-up-hide` trigger
         * was swallowed by `if (event.type === 'click' && this.eventMouseEnterHappened)`
         * and the popover simply did not open. Below 990px the visible trigger and
         * its click handler are the whole interaction.
         */
        if (this.infoButtonDesktop && this.mql.matches) {
          this.infoButtonDesktop.addEventListener('mouseenter', this.togglePopover.bind(this));
          this.infoButtonDesktop.addEventListener('mouseleave', this.closePopover.bind(this));
        }

        if (this.infoButtonDesktop) {
          this.infoButtonDesktop.addEventListener('click', this.togglePopover.bind(this));
          this.infoButtonDesktop.addEventListener('focusout', this.closePopover.bind(this));
        }

        if (this.infoButtonMobile) {
          this.infoButtonMobile.addEventListener('click', this.togglePopover.bind(this));
        }

        /*
         * One Escape listener for the lifetime of the element.
         *
         * The previous version attached a fresh `keyup` handler to the button on
         * every open and never removed it, so N open/close cycles left N handlers
         * on the button doing the same work. Binding once on the element also fixes
         * a functional gap rather than just the leak: because the old handler lived
         * on the trigger, Escape did nothing once focus had moved into the popover
         * (e.g. onto its close button) -- the only place it was needed most.
         *
         * `stopPropagation` because a popover opened inside a `details` element
         * would otherwise also collapse it (see `onKeyUpEscape` in `global.js`).
         */
        this.addEventListener('keydown', (event) => this.onEscape(event));
      }

      /* Whichever trigger is on screen at the current width. */
      get activeButton() {
        return this.infoButtonDesktop && this.mql.matches ? this.infoButtonDesktop : this.infoButtonMobile;
      }

      onEscape(event) {
        if (event.key !== 'Escape' && event.code !== 'Esc') return;
        const button = this.activeButton;
        if (!button || button.getAttribute('aria-expanded') !== 'true') return;
        event.stopPropagation();
        // `returnFocus` sends focus back to the trigger, which is where the
        // keyboard user opened it from -- closing a popover and leaving focus on
        // the body loses the place they were in.
        this.closePopover(event, { returnFocus: true });
      }

      togglePopover(event) {
        event.preventDefault();
        if (event.type === 'mouseenter') {
          this.eventMouseEnterHappened = true;
        }

        if (event.type === 'click' && this.eventMouseEnterHappened) {
          // Hover already opened it, so this click is the same interaction rather
          // than a toggle. Cleared here because leaving it set swallowed every
          // later click too -- including the one the shopper makes to close it.
          this.eventMouseEnterHappened = false;
          return;
        }

        const button = this.activeButton;
        if (!button) return;
        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        if ((this.mql.matches && !isExpanded) || event.type === 'click') {
          button.setAttribute('aria-expanded', !isExpanded);

          this.popoverInfo.toggleAttribute('hidden');

          button.classList.toggle('quantity-popover__info-button--open');

          this.infoButtonDesktop?.classList.add('quantity-popover__info-button--icon-only--animation');
        }
      }

      closePopover(event, { returnFocus = false } = {}) {
        event?.preventDefault?.();

        /*
         * `relatedTarget` is null for the Escape path and for a `mouseleave`, in
         * which case `contains(null)` is false for both containers and the popover
         * closes -- which is the intended outcome. It is only non-null for the
         * `focusout` path, where moving between the trigger and the popover itself
         * must not close it.
         */
        const relatedTarget = event?.relatedTarget ?? null;
        const isButtonChild = this.infoButtonDesktop?.contains(relatedTarget) ?? false;
        const isPopoverChild = this.popoverInfo?.contains(relatedTarget) ?? false;

        if (!isButtonChild && !isPopoverChild) {
          const button = this.activeButton;
          if (button) {
            button.setAttribute('aria-expanded', 'false');
            button.classList.remove('quantity-popover__info-button--open');
            // Return focus to the trigger only when asked. The `focusout` and
            // `mouseleave` paths are the browser moving focus on its own, and
            // yanking it back there would fight the shopper's next Tab.
            if (returnFocus) button.focus({ preventScroll: true });
          }
          this.popoverInfo?.setAttribute('hidden', '');
          this.infoButtonDesktop?.classList.remove('quantity-popover__info-button--icon-only--animation');
        }

        this.eventMouseEnterHappened = false;
      }
    }
  );
}
